/**
 * Welle 119 — Rechnungsausgangsbuch (Lasche „ReAusgBuch", imho_v2.re_ausg_buch) als eigener Reiter:
 * Suche, Filter Nummernkreis / Jahr / Status; öffnen, PDF, duplizieren (alte Rechnung als Vorlage), stornieren, löschen.
 * Welle 123: stornieren = Stornorechnung stellen; löschen auch gestellter Rechnungen (komplett, Nummer wird frei).
 */
import React, { useCallback, useEffect, useState } from "react";
import { API, json, euro, deDatum } from "./hilfen";
import "../mietverwaltung/Tab5Bescheinigungen.css";
import "./Rechnungen.css";

export default function Ausgangsbuch({ onOeffnen, onDuplizieren, neuLaden = 0 }) {
  const [liste, setListe] = useState([]);
  const [q, setQ] = useState("");
  const [kreis, setKreis] = useState("");
  const [jahr, setJahr] = useState("");
  const [status, setStatus] = useState("");
  const [kreise, setKreise] = useState([]);
  const [meldung, setMeldung] = useState(null);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(() => {
    const p = new URLSearchParams({ q, kreis, status, limit: 300 });
    if (jahr) p.set("jahr", jahr);
    fetch(`${API}/liste?${p}`).then(json).then((d) => setListe(d.rechnungen || [])).catch(() => setListe([]));
  }, [q, kreis, jahr, status]);
  useEffect(() => { const t = setTimeout(laden, 250); return () => clearTimeout(t); }, [laden, neuLaden]);
  useEffect(() => { fetch(`${API}/kreise`).then(json).then((d) => setKreise(d.kreise || [])).catch(() => {}); }, [neuLaden]);

  const pdf = async (row) => {
    setBusy(true);
    try {
      let r;
      if (row.rg_typ === "Nenngeld") {
        r = await fetch(`${API.replace("/rechnung", "/nenngeld")}/${row.id}/pdf`);   // Welle 124
      } else {
        const d = await json(await fetch(`${API}/${row.id}`));
        r = await fetch(`${API}/pdf?ablegen=0`, { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ daten: d }) });
      }
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
    setBusy(false);
  };
  // Welle 123: Stornieren erzeugt eine Stornorechnung (neue Nummer, negative Beträge, Batch Output)
  const storno = async (row) => {
    if (!window.confirm(`${row.rg_nr} stornieren?\n\nEs wird eine Stornorechnung mit neuer Nummer und negativen Beträgen `
      + "gestellt und in den Batch Output gelegt (an eigene Einheiten auch als Eingangsrechnung). "
      + "Die Rechnung bleibt im Buch mit Status „storniert“, übernommene Arbeitszeit wird wieder frei.")) return;
    setBusy(true);
    try {
      const d = await json(await fetch(`${API}/${row.id}/storno`, { method: "POST" }));
      setMeldung({ ok: true, text: `${row.rg_nr} storniert — Stornorechnung ${d.storno_nr} gestellt: ${d.dateiname}`
        + (d.eingang ? " (dazu die Eingangsrechnung)" : "") });
      laden();
    } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
    setBusy(false);
  };
  const loeschen = async (row) => {
    if (row.status === "entwurf" || !row.formular) {
      if (!window.confirm(`${row.status === "entwurf" ? "Entwurf" : "Zeile"} ${row.rg_nr || row.id} löschen?`)) return;
      try {
        await json(await fetch(`${API}/${row.id}${row.status === "entwurf" ? "" : "?hart=1&dry_run=0"}`, { method: "DELETE" }));
        laden();
      } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
      return;
    }
    // Welle 123: gestellte/stornierte Rechnung komplett löschen — erst zeigen, was alles wegfällt
    setBusy(true);
    try {
      const plan = await json(await fetch(`${API}/${row.id}?hart=1&dry_run=1`, { method: "DELETE" }));
      const liste = [`die Zeile im Rechnungsausgangsbuch (${plan.rg_nr}) mit allen Positionen`,
        ...(plan.dateien || []).map((p) => `Datei ${String(p).split("/").pop()}`),
        plan.dokument_id ? "der Eintrag bei den Dokumenten" : "", plan.google ? "die Google-Lasche" : "",
        "Anlagen/Fotos der Rechnung", plan.storno_zu_nr ? `danach ist ${plan.storno_zu_nr} wieder „gestellt“` : ""].filter(Boolean);
      if (!window.confirm(`${plan.rg_nr} KOMPLETT löschen? Das lässt sich nicht rückgängig machen — die Nummer wird wieder frei.\n\n`
        + `Gelöscht wird:\n• ${liste.join("\n• ")}`)) { setBusy(false); return; }
      if (row.rg_typ !== "Nenngeld" && window.confirm("Vorher als Vorlage sichern? (OK = als Entwurf mit allen Positionen behalten)")) {
        const d = await json(await fetch(`${API}/${row.id}/duplizieren`, { method: "POST" }));
        await json(await fetch(`${API}/entwurf`, { method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ daten: d }) }));
      }
      const mitStorno = plan.storno_id && window.confirm(`Die Stornorechnung ${plan.storno_nr} auch löschen?`);
      const r = await json(await fetch(`${API}/${row.id}?hart=1&dry_run=0&mit_storno=${mitStorno ? 1 : 0}`, { method: "DELETE" }));
      setMeldung({ ok: true, text: `${plan.rg_nr} gelöscht — die Nummer ist wieder frei.`
        + ((r.hinweise || []).length ? ` Hinweise: ${r.hinweise.join(" · ")}` : "") });
      laden();
    } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
    setBusy(false);
  };

  const jetzt = new Date().getFullYear();
  const summe = liste.filter((r) => r.status !== "storniert" && r.status !== "entwurf")
    .reduce((a, r) => a + (r.brutto ?? r.netto ?? 0), 0);

  return (
    <div className="rg-wrap">
      <div className="mb-sektion">
        <div className="mb-sektion-titel">Rechnungsausgangsbuch (Lasche „ReAusgBuch“)</div>
        <div className="rg-buch-filter">
          <input className="mb-input" value={q} placeholder="suchen: Nummer, Name, Objekt, Inhalt …" onChange={(e) => setQ(e.target.value)} />
          <select className="mb-input" value={kreis} onChange={(e) => setKreis(e.target.value)}>
            <option value="">alle Nummernkreise</option>
            {kreise.map((k) => <option key={k} value={k}>{k === "OBJ" ? "Objekte (alle, ein Kreis)" : (k === "NG" ? "Nenngeld (TCH)" : k)}</option>)}
          </select>
          <select className="mb-input" value={jahr} onChange={(e) => setJahr(e.target.value)}>
            <option value="">alle Jahre</option>
            {[0, 1, 2, 3, 4, 5].map((i) => <option key={i} value={jetzt - i}>{jetzt - i}</option>)}
          </select>
          <select className="mb-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">alle Status</option>
            <option value="entwurf">Entwurf</option><option value="gestellt">gestellt</option>
            <option value="storniert">storniert</option><option value="excel">aus der Excel</option>
          </select>
          <span className="rg-klein" style={{ margin: 0 }}>{liste.length} Zeilen · gestellt {euro(summe)}</span>
        </div>
        {meldung && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}
        <div className="rg-tabelle-rahmen">
          <table className="rg-tabelle rg-buch">
            <thead><tr><th>Rg-Nr</th><th>Datum</th><th>Absender</th><th>Objekt</th><th>Debitor · Name</th><th>Inhalt</th><th className="r">Brutto</th><th>Status</th><th /></tr></thead>
            <tbody>
              {liste.map((r) => (
                <tr key={r.id} className={r.status === "storniert" ? "rg-storniert" : ""}>
                  <td>{r.rg_nr || (r.status === "entwurf" ? <i className="rg-klein">Entwurf</i> : "—")}</td><td>{deDatum(r.datum)}</td><td>{r.absender}</td><td>{r.objekt}</td>
                  <td>{[r.debitor, r.name].filter(Boolean).join(" · ")}</td><td className="rg-inhalt">{r.inhalt}</td>
                  <td className="r">{r.brutto != null ? euro(r.brutto) : (r.netto != null ? euro(r.netto) : "")}</td>
                  <td>{r.status || (r.formular ? "" : "Excel")}
                    {r.rg_typ === "Storno" && <div className="rg-klein" style={{ margin: 0 }}>Storno zu {r.storno_zu_nr}</div>}
                    {r.storno_nr && <div className="rg-klein" style={{ margin: 0 }}>durch {r.storno_nr}</div>}
                    {r.rg_typ === "Nenngeld" && <div className="rg-klein" style={{ margin: 0 }}>Nenngeld{r.versand_am ? ` · gesendet ${deDatum(r.versand_am)}` : ""}</div>}
                    {r.rg_typ !== "Nenngeld" && r.versand_am && <div className="rg-klein" style={{ margin: 0 }} title={r.versand_an}>✉ gesendet {deDatum(String(r.versand_am).slice(0, 10))} an {r.versand_an}</div>}</td>
                  <td className="rg-aktionen">
                    {r.formular && r.rg_typ !== "Nenngeld" && <button className="mb-link" onClick={() => onOeffnen(r.id)}>öffnen</button>}
                    {r.formular && r.status !== "entwurf" && <button className="mb-link" disabled={busy} onClick={() => pdf(r)}>PDF</button>}
                    {r.formular && ["gestellt", "storniert"].includes(r.status) && !["Nenngeld"].includes(r.rg_typ) && !r.versand_am
                      && <button className="mb-link" title="Öffnen und per E-Mail senden (rechnung@immo.holthey.de)" onClick={() => onOeffnen(r.id)}>✉ per Mail</button>}
                    {r.rg_typ !== "Nenngeld" && <button className="mb-link" title="Als Vorlage für eine neue Rechnung nehmen" onClick={() => onDuplizieren(r.id)}>duplizieren</button>}
                    {r.status === "gestellt" && !["Storno", "Nenngeld"].includes(r.rg_typ) && r.formular && <button className="mb-link" disabled={busy} onClick={() => storno(r)}>stornieren</button>}
                    {(r.formular || r.status === "entwurf") && <button className="mb-link rg-loeschen" disabled={busy} onClick={() => loeschen(r)}
                      title={r.status === "entwurf" ? "Entwurf löschen" : "Komplett löschen (Nummer wird frei)"}>löschen</button>}
                  </td>
                </tr>
              ))}
              {!liste.length && <tr><td colSpan={9} className="rg-klein">Keine Einträge.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
